// Report upload service: 4-step presigned-S3 flow with multipart fallback for local FileSystemStorage.
//
// Used by both the online submit (patientScreeningWrapper.handleSubmitReport) and the offline
// syncData per-report loop, so the same orchestrator runs in both places.
//
// Flow (production / S3):
//   1. POST <api>/api/reports/addreport/                 (JSON metadata only)            -> { uuid, ... }
//   2. POST <api>/api/reports/<uuid>/presigned-upload/   ([{ side, filename, content_type }])
//      -> { uploads: [{ side, method, url, headers, key, expires_in }] }
//   3. PUT <s3.url> with item.headers, body = blob       (one per side)
//   4. POST <api>/api/reports/addreport/                 ({ report_uuid, s3_keys: { left, right } })
//      -> full report row (response from step 1 or step 4 — caller treats step 4 as authoritative)
//
// On HTTP 400 / 404 from the presign endpoint we throw PresignNotSupportedError so the caller can
// fall back to the legacy multipart POST (one request, file fields). That covers local
// FileSystemStorage where presigning isn't wired up.

import axios from 'axios';

export class PresignNotSupportedError extends Error {
    constructor(status, detail) {
        super(`Presigned upload not supported (HTTP ${status})`);
        this.name = 'PresignNotSupportedError';
        this.status = status;
        this.detail = detail;
    }
}

/** Resume path needs presigned URLs; multipart fallback would create a duplicate report. */
export class ResumeUploadBlockedError extends Error {
    constructor(message = 'Cannot resume upload without presigned URLs on this server.') {
        super(message);
        this.name = 'ResumeUploadBlockedError';
    }
}

/**
 * DRF expects integers for diabetes duration on JSON POST; empty string "" fails validation.
 * Legacy multipart often sent ""; JSON is stricter. Only add keys when diabetes === "Yes" and
 * the value parses to a non‑negative integer; otherwise omit (backend treats as unspecified).
 */
function parseOptionalNonNegativeInt(raw) {
    if (raw === undefined || raw === null) return undefined;
    const s = String(raw).trim();
    if (s === '') return undefined;
    const n = parseInt(s, 10);
    if (!Number.isFinite(n) || n < 0) return undefined;
    return n;
}

/** Mutates target (report metadata): sets diabetes_duration_years/months only when valid ints. */
export function applyDiabetesDurationForReportApi(target, diabetes, yearsRaw, monthsRaw) {
    if (diabetes !== 'Yes') return;
    const y = parseOptionalNonNegativeInt(yearsRaw);
    const m = parseOptionalNonNegativeInt(monthsRaw);
    if (y !== undefined) target.diabetes_duration_years = y;
    if (m !== undefined) target.diabetes_duration_months = m;
}

/** image/jpg → image/jpeg; default image/jpeg. Server accepts jpeg/png/webp. */
function contentTypeOf(blob) {
    const t = (blob && blob.type) ? String(blob.type).toLowerCase() : '';
    if (t === 'image/jpg') return 'image/jpeg';
    if (t === 'image/jpeg' || t === 'image/png' || t === 'image/webp') return t;
    return 'image/jpeg';
}

function defaultExtensionFor(contentType) {
    if (contentType === 'image/png') return 'png';
    if (contentType === 'image/webp') return 'webp';
    return 'jpg';
}

/**
 * Sanitize a filename to keep only [A-Za-z0-9._-]; strip path components.
 * Falls back to {side}.{ext} if no usable basename can be derived.
 */
function buildSafeFilename(blob, originalName, side) {
    const ct = contentTypeOf(blob);
    const fallback = `${side}.${defaultExtensionFor(ct)}`;

    if (!originalName || typeof originalName !== 'string') return fallback;

    const basename = originalName.split(/[\\/]/).pop();
    if (!basename) return fallback;

    const safe = basename.replace(/[^A-Za-z0-9._-]/g, '_');
    if (!safe || safe === '.' || safe === '..') return fallback;
    return safe;
}

/**
 * Raw fetch PUT to the presigned URL. S3 returns empty 200; we only need to detect non-2xx.
 * We deliberately do NOT use axios here — axios may add headers that break the signed URL.
 */
async function putToS3(item, blob) {
    const res = await fetch(item.url, {
        method: item.method || 'PUT',
        headers: item.headers || { 'Content-Type': blob.type || 'application/octet-stream' },
        body: blob,
    });
    if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(`S3 PUT ${item.side} failed: ${res.status} ${res.statusText}${txt ? ' — ' + txt.slice(0, 200) : ''}`);
    }
}

function buildUploadDescriptors({ leftBlob, rightBlob, leftName, rightName }) {
    const out = [];
    if (leftBlob) {
        const ct = contentTypeOf(leftBlob);
        out.push({
            side: 'left',
            filename: buildSafeFilename(leftBlob, leftName, 'left'),
            content_type: ct,
            blob: leftBlob,
        });
    }
    if (rightBlob) {
        const ct = contentTypeOf(rightBlob);
        out.push({
            side: 'right',
            filename: buildSafeFilename(rightBlob, rightName, 'right'),
            content_type: ct,
            blob: rightBlob,
        });
    }
    return out;
}

/**
 * Steps 1–4 (or resume 2–4 when existingServerUuid is set).
 *
 * Throws PresignNotSupportedError when the presign endpoint returns 400/404 (non‑resume) so the
 * caller can fall back to multipart. On resume, same status throws ResumeUploadBlockedError instead
 * (multipart would duplicate the server row). All other errors propagate.
 *
 * @param {string} [existingServerUuid] Skip metadata POST; run presign→PUT→attach only.
 * @param {(uuid: string) => void | Promise<void>} [onReportUuidCreated] After metadata create; persist uuid before presign/POT.
 */
export async function submitReportWithPresign({
    apiBase,
    headers,
    metadata,
    leftBlob,
    rightBlob,
    leftName,
    rightName,
    existingServerUuid,
    onReportUuidCreated,
}) {
    const uploads = buildUploadDescriptors({ leftBlob, rightBlob, leftName, rightName });
    const resuming = Boolean(existingServerUuid && String(existingServerUuid).trim());
    let uuid;

    if (resuming) {
        uuid = String(existingServerUuid).trim();
    } else {
        // Step 1: create row with JSON metadata.
        const createRes = await axios.post(
            `${apiBase}/api/reports/addreport/`,
            metadata,
            { headers: { ...headers, 'Content-Type': 'application/json' } },
        );

        uuid = createRes?.data?.uuid;
        if (!uuid) {
            throw new Error('addreport metadata response missing uuid');
        }

        if (typeof onReportUuidCreated === 'function') {
            await onReportUuidCreated(uuid);
        }

        // No images at all — nothing to attach; treat metadata response as final.
        if (uploads.length === 0) {
            return createRes;
        }
    }

    if (uploads.length === 0 && resuming) {
        return { data: { uuid }, status: 200 };
    }

    // Step 2: presigned URLs.
    let presignRes;
    try {
        presignRes = await axios.post(
            `${apiBase}/api/reports/${uuid}/presigned-upload/`,
            { uploads: uploads.map(({ side, filename, content_type }) => ({ side, filename, content_type })) },
            { headers: { ...headers, 'Content-Type': 'application/json' } },
        );
    } catch (err) {
        const status = err?.response?.status;
        if (status === 400 || status === 404) {
            if (resuming) {
                throw new ResumeUploadBlockedError(
                    'Cannot resume this report: presigned upload is not supported on this server.',
                );
            }
            throw new PresignNotSupportedError(status, err?.response?.data);
        }
        throw err;
    }

    const presignedUploads = presignRes?.data?.uploads || [];
    if (!Array.isArray(presignedUploads) || presignedUploads.length !== uploads.length) {
        throw new Error('presigned-upload response: uploads length mismatch');
    }

    // Step 3: PUT raw bytes per side. fail_all — any failure aborts the whole flow.
    const sideToBlob = Object.fromEntries(uploads.map((u) => [u.side, u.blob]));
    for (const item of presignedUploads) {
        const blob = sideToBlob[item.side];
        if (!blob) throw new Error(`presign returned unknown side: ${item.side}`);
        await putToS3(item, blob);
    }

    // Step 4: attach S3 keys to the report.
    const s3Keys = {};
    for (const item of presignedUploads) {
        if (item.key && (item.side === 'left' || item.side === 'right')) {
            s3Keys[item.side] = item.key;
        }
    }

    const attachRes = await axios.post(
        `${apiBase}/api/reports/addreport/`,
        { report_uuid: uuid, s3_keys: s3Keys },
        { headers: { ...headers, 'Content-Type': 'application/json' } },
    );

    return attachRes;
}

/**
 * Legacy single-request multipart POST (mirrors the old behavior). Used as a fallback when
 * the backend's presigned endpoint is not available (local FileSystemStorage).
 */
export async function multipartFallback({
    apiBase,
    headers,
    metadata,
    leftBlob,
    rightBlob,
    leftName,
    rightName,
}) {
    const formData = new FormData();
    for (const [k, v] of Object.entries(metadata || {})) {
        if (v === undefined || v === null) continue;
        if (typeof v === 'object') {
            formData.append(k, JSON.stringify(v));
        } else {
            formData.append(k, v);
        }
    }
    if (leftBlob) {
        formData.append('left', leftBlob, buildSafeFilename(leftBlob, leftName, 'left'));
    }
    if (rightBlob) {
        formData.append('right', rightBlob, buildSafeFilename(rightBlob, rightName, 'right'));
    }
    return axios.post(`${apiBase}/api/reports/addreport/`, formData, { headers });
}
